import React, { useMemo, useState } from 'react';
import useStore from '@components/MainStage/store';
import { ProjectDrawerTypes } from '@constants/entities/drawers';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useProjectId from '@hooks/useProjectId';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { useUpdateNodesActionMutation } from '@store/services/nodes';
import {
  INodeActionOutcome,
  NodeActionResult,
  NodeActionType,
  NodeDTO,
} from '@store/services/nodes/types';
import { NODE_ACTIONS } from '@utils/nodeActions';

import {
  CreatableRelationPair,
  filterPairsByIncludedOutcomes,
  isCreateRelationshipsVisible,
  resolveCreatableRelationPairs,
} from './createRelationshipsMenu.helpers';
import { NodeActionConfirmModal } from './NodeActionConfirmModal';

export const useCreateRelationships = (
  targetIds: string[],
  onCloseMenu: () => void,
) => {
  const {
    getNodeById,
    nodes,
    onConnect,
    selectEdge,
    clearLocalEdges,
    clearLocalNodes,
  } = useStore();
  const {
    setDrawer,
    toolbox: { mode },
  } = useProject();
  const { selectedAssetId } = useAssets();
  const projectId = useProjectId(true);
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [updateNodesAction, { isLoading }] = useUpdateNodesActionMutation();
  const [pendingOutcomes, setPendingOutcomes] = useState<
    INodeActionOutcome[] | null
  >(null);

  const config = NODE_ACTIONS[NodeActionType.CreateRelationships];
  const targetIdsKey = targetIds.join(',');

  const targetNodes = useMemo(
    () =>
      targetIds
        .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetIdsKey, nodes, getNodeById],
  );

  const getNodeType = (id: string) =>
    (getNodeById(id)?.data?.dto as NodeDTO | undefined)?.type;

  const pairs = useMemo(
    () => resolveCreatableRelationPairs(targetNodes, getNodeType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetNodes, nodes],
  );

  const nameById = useMemo(
    () => new Map(targetNodes.map((node) => [node.id, node.name])),
    [targetNodes],
  );

  const isVisible = isCreateRelationshipsVisible(targetNodes, getNodeType);

  const openDrawerForPair = (pair: CreatableRelationPair) => {
    clearLocalEdges();
    clearLocalNodes();

    const edgeId = onConnect({
      source: pair.parent_id,
      target: pair.child_id,
      sourceHandle: null,
      targetHandle: null,
    });

    if (edgeId) {
      selectEdge(edgeId);
      setDrawer({ type: ProjectDrawerTypes.AddRelationship });
    }
    setPendingOutcomes(null);
    onCloseMenu();
  };

  const run = async (force: boolean) => {
    if (!mode) return;

    const response = await updateNodesAction({
      nodeIds: targetIds,
      actionType: NodeActionType.CreateRelationships,
      mode,
      assetId: selectedAssetId || undefined,
      force,
      projectId,
    }).unwrap();

    if (!response.applied) {
      setPendingOutcomes(response.outcomes);
      return;
    }

    saveResponseToRedux(response);
    const count = response.created_count ?? pairs.length;
    showToast(
      config?.successMessageWithCount?.(count) ?? config?.successMessage ?? '',
      'success',
    );
    setPendingOutcomes(null);
    onCloseMenu();
  };

  const start = () => {
    if (isLoading || !pairs.length) return;
    if (targetIds.length === 2 && pairs.length === 1) {
      openDrawerForPair(pairs[0]);
      return;
    }
    run(false);
  };

  const outcomes = pendingOutcomes ?? [];
  const nameOf = (id: string) => nameById.get(id) ?? id;
  const toItem = (outcome: INodeActionOutcome) => ({
    id: outcome.node_id,
    name: nameOf(outcome.node_id),
  });

  const includedPairs = filterPairsByIncludedOutcomes(pairs, outcomes);

  const pairItems = includedPairs.map(({ parent_id, child_id }) => ({
    id: `${parent_id}-${child_id}`,
    name: `${nameOf(parent_id)} → ${nameOf(child_id)}`,
  }));

  const onConfirm = () => {
    if (isLoading) return;
    if (includedPairs.length === 1) {
      openDrawerForPair(includedPairs[0]);
      return;
    }
    run(true);
  };

  const modal = (
    <NodeActionConfirmModal
      open={pendingOutcomes !== null}
      title={config?.menuLabel ?? ''}
      text={config?.menuText ?? ''}
      toChange={pairItems}
      toChangeTitle={config?.menuToChangeTitle ?? ''}
      skipped={outcomes
        .filter(
          (o) =>
            o.result === NodeActionResult.Ineligible ||
            o.result === NodeActionResult.Blocked,
        )
        .map(toItem)}
      toSkipTitle={config?.menuToSkipTitle ?? ''}
      confirmName={config?.menuConfirmName ?? ''}
      contentSx={{ gap: 3 }}
      onBack={() => setPendingOutcomes(null)}
      onConfirm={onConfirm}
    />
  );

  return { isVisible, isLoading, start, modal };
};
